import React, { useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import type { NodeProps } from '@xyflow/react';
import { Card, Typography, IconButton, Stack, Dropdown, Menu, MenuItem, MenuButton, Divider } from '@mui/joy';
import {
  Play as PlayArrowIcon,
  DotsThreeVertical as MoreVertIcon,
  PencilSimple as EditIcon,
  Trash as DeleteIcon,
  ArrowsOutCardinal as MoveIcon,
  ArrowDown as MoveAllIcon
} from '@phosphor-icons/react';
import type { AutomationAction } from '../../../types/automations';

interface ActionNodeData {
  action: AutomationAction;
  onEdit: () => void;
  onDelete: () => void;
  onMove?: () => void;
  onMoveAll?: () => void;
}

export default function ActionNode({ data }: NodeProps) {
  const { action, onEdit, onDelete, onMove, onMoveAll } = data as unknown as ActionNodeData;
  const [menuOpen, setMenuOpen] = useState(false);

  const getActionLabel = (type: string) => {
    const labels: Record<string, string> = {
      send_email: 'Send Email',
      create_notification: 'Create Notification',
      create_task: 'Create Task',
      send_sms: 'Send SMS',
      assign_to_user: 'Assign to User',
      create_contact: 'Create Contact',
      add_tag: 'Add Tag',
      remove_tag: 'Remove Tag',
      add_note: 'Add Note',
      modify_opportunity_confidence: 'Modify Opportunity Confidence',
      create_opportunity: 'Create Opportunity',
      send_review_request: 'Send Review Request',
    };
    return labels[type] || type;
  };

  return (
    <>
      <Handle type="target" position={Position.Top} />
      <Handle type="source" position={Position.Bottom} />
      <Card
        variant="outlined"
        sx={{
          minWidth: 250,
          bgcolor: 'primary.softBg',
          borderColor: 'primary.500',
          borderWidth: 2,
          cursor: 'default', // Prevent drag cursor
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <PlayArrowIcon color="var(--joy-palette-primary-500)" />
          <Typography level="title-md" sx={{ flex: 1 }}>
            {getActionLabel(action.action_type)}
          </Typography>
          <Dropdown open={menuOpen} onOpenChange={(event, open) => setMenuOpen(open)}>
            <MenuButton
              slots={{ root: IconButton }}
              slotProps={{ root: { 
                size: 'sm', 
                variant: 'plain',
                onClick: (e: React.MouseEvent) => {
                  e.stopPropagation();
                }
              } }}
            >
              <MoreVertIcon size={18} />
            </MenuButton>
            <Menu placement="bottom-end">
              <MenuItem onClick={() => { onEdit(); setMenuOpen(false); }}>
                <EditIcon size={18} style={{ marginRight: 8 }} />
                Edit
              </MenuItem>
              {onMove && (
                <MenuItem onClick={() => { onMove(); setMenuOpen(false); }}>
                  <MoveIcon size={18} style={{ marginRight: 8 }} />
                  Move
                </MenuItem>
              )}
              {onMoveAll && (
                <MenuItem onClick={() => { onMoveAll(); setMenuOpen(false); }}>
                  <MoveAllIcon size={18} style={{ marginRight: 8 }} />
                  Move All From Here
                </MenuItem>
              )}
              <Divider />
              <MenuItem color="danger" onClick={() => { onDelete(); setMenuOpen(false); }}>
                <DeleteIcon size={18} style={{ marginRight: 8 }} />
                Delete
              </MenuItem>
            </Menu>
          </Dropdown>
        </Stack>
        {action.config && Object.keys(action.config).length > 0 && (
          <Typography level="body-sm" sx={{ mt: 1 }}>
            Configured
          </Typography>
        )}
      </Card>
    </>
  );
}
